import { Order, Status, SingleOrderItem } from './order';
import { PickupAddress } from './pickupAddress';

export enum PickupPageTabEnum {
  Pickups = 'pickups',
  RTO = 'return_to_origin',
}

// Preferred timing within a slot (v1.1). The merchant picks WHEN inside their
// guaranteed slot window they'd like the pickup, as one of three coarse
// buckets — never a clock time. `null` (absent) means "Anytime" (no
// preference), which is a complete, valid default state. Each bucket maps to a
// third of the slot window internally (fleet-only); the merchant only ever
// sees the labels Anytime / Early / Mid / Late.
export type PreferredTiming = 'early' | 'mid' | 'late';

// Define the object structure of the response
export interface PickupObject {
  items: PickupItem[];
  page: number;
  total: number;
  pages: number;
  perPage: number;
}

// Define the full response structure
export interface PickupResponse {
  status: string;
  code: number;
  message: string;
  object: PickupObject;
}

// Real shape of PickUp::getSlotAttribute() (core-service
// app/Models/PickUp/PickUp.php) — the ONLY fields it ever serializes are
// code/label/window_from/window_to. `is_next_day` is a SEPARATE sibling
// accessor (getIsNextDayAttribute) appended alongside `slot`, never nested
// inside it — see PickupItem.is_next_day below. Shared here so every FE
// consumer of an appended `slot` (pickups list/show, and the single-ship
// auto-advance toast) reads the same real contract instead of each
// inventing its own shape.
export interface PickUpSlotSummary {
  code: string | null;
  label: string;
  window_from: string;
  window_to: string;
  // v1.1 preferred-timing within slot: the backend stamps the merchant's
  // requested `preferred_timing` bucket (null = Anytime, else early/mid/late)
  // onto each pickup's serialized `slot`. NEVER derive it on the client — the
  // server owns the thirds/window math.
  preferred_timing?: PreferredTiming | null;
}

export interface PickupItem {
  uid: string;
  platform_code: string | null;
  date: string | null;
  created_at: string;
  updated_at: string;
  deleted_at: string | null;
  driver: DriverItem | null;
  status: Status | null;
  pick_up_address: PickupAddress | null;
  task: string | null;
  label_dispatched_at: string | null;
  missed_orders_count: number | null;
  orders_count: number | null;
  slot?: PickUpSlotSummary | null;
  slot_state?: string | null;
  is_next_day?: boolean;
  slot_response_open?: boolean;
  slot_response_deadline?: string | null;
  operational_date?: string | null;
}

export interface PickupSlot {
  code: string;
  label: string;
  window_from: string; // 'HH:MM'
  window_to: string;
  is_next_day: boolean;
  // v2-task-10fe (T10-BE): server-only signals — NEVER re-derive these on
  // the client. `is_closing_soon` is true for AT MOST the soonest still-open
  // slot; `pickup_date` is the actual operational date (YYYY-MM-DD) a pickup
  // for this slot would land on after closed-day skips; `moved_for_holiday`
  // is true when that date was pushed past one or more closed days.
  is_closing_soon?: boolean;
  pickup_date?: string;
  moved_for_holiday?: boolean;
  // v1.1 preferred-timing within slot: the static set of timing buckets the
  // merchant may request inside this slot's window. Always ['early','mid','late']
  // — the three-bucket model is slot-independent, so this exists mainly to let
  // the client feature-detect timing support without hard-coding it.
  preferred_timing_options?: PreferredTiming[];
}

// Enveloped result of POST /merchants/pick-ups/{uid}/slot-response
// (`response.data.object`). On a "defer" response the backend moves the pickup
// back to its ORIGINAL slot on the next operational day and echoes that landing
// slot in `resulting_slot`; the FE surfaces THAT slot in the defer-confirmation
// toast rather than any locally-known rolled-to values. A `superseded: true`
// response carries only `{ superseded: true }` (no resulting_slot).
export interface SlotResponseResult {
  superseded: boolean;
  is_next_day?: boolean;
  resulting_slot?: {
    code: string;
    label: string;
    window_from: string;
    window_to: string;
  } | null;
}

export interface PickupSlotsResponse {
  status: string;
  code: number;
  message: string;
  // GET /merchants/pick-up-slots `object` is the bare slots ARRAY — a
  // backward-compatible contract that never breaks already-deployed clients.
  // Each slot carries `preferred_timing_options`; the merchant's SAVED
  // preferred timing rides on GET /user (setting.pickup_preferred_timing),
  // NOT on this envelope.
  object: PickupSlot[];
}

export interface DriverItem {
  uid: string;
  first_name: string;
  last_name: string;
  phone: string;
  email: string | null;
  type: string;
  last_position?: {
    lat: string;
    lng: string;
  };
}

export interface PickupIdRequest {
  id: string;
}

// Slot-change consolidation: PUT /pick-up-slots/preference echoes back how
// many of the merchant's open same-day pickups moved to the new slot and how
// many were route-locked and left in place.
//
// Routed-pickup relocation (backend fix): `relocated` counts pickups that Fleet
// had ALREADY routed (slot_state route_included/route_appended) which the backend
// now MOVES onto the new slot and re-plans the pickup agent for — instead of the
// old behavior of silently leaving them behind (which is what `skipped` used to
// cover). Surfaced so the dashboard can warn the merchant their routed pickup was
// re-assigned. Optional for backward compatibility with pre-fix responses.
export interface ReslotSummary {
  moved: number;
  skipped: number;
  relocated?: number;
}

// Enveloped result of PATCH /merchants/pick-ups/{uid}/time-slot
// (`response.data.object`). `relocated_from_route` is true when THIS edit moved
// an already-routed pickup (slot_state route_included/route_appended) onto the
// new slot — Fleet re-plans the pickup agent onto that slot — so the dashboard
// warns the merchant instead of showing a plain success. `is_next_day` mirrors
// the same sibling signal the other slot surfaces read.
export interface TimeSlotEditResult {
  pick_up?: PickupItem | null;
  is_next_day?: boolean;
  relocated_from_route?: boolean;
}

export interface PickUpParams {
  saved_pick_up_address_uid?: string;
  date?: string;
  order_uids: string[];
  time_slot_code?: string;
  // v1.1: when the mandatory slot gate is satisfied via the inline picker, the
  // retried ship carries the merchant's chosen preferred timing bucket
  // (null = Anytime) alongside the slot code.
  preferred_timing?: PreferredTiming | null;
}

// v2-task-9: T3 (backend) stamps each pick-up returned by
// POST /merchants/pick-ups/request with transient `auto_advanced` /
// `auto_advanced_from` attributes when the merchant's auto-select toggle
// silently booked a different slot than requested. `slot` uses the SAME
// PickUpSlotSummary shape as show()/index() (PickUp::getSlotAttribute) —
// it is only populated once the pickup's `slot` accessor is appended on
// the request() response too (T9 review Critical #2 backend fix, landing
// separately). Until then `slot` is absent/null and the toast below stays
// silent — see the `!advanced.slot` guard in showAutoAdvanceToastIfAny.
export interface PickUpAutoAdvanceItem {
  auto_advanced?: boolean;
  auto_advanced_from?: string | null;
  slot?: PickUpSlotSummary | null;
}

export interface PickupDataResponse {
  status: string;
  code: number;
  message: string;
  object?: PickUpAutoAdvanceItem[];
}

export interface PickUpSingleResponse {
  status: string;
  code: number;
  message: string;
  object: {
    uid: string;
    platform_code: string;
    date: string | null;
    created_at: string;
    updated_at: string;
    deleted_at: string | null;
    orders_count: number | null;
    shipment_fee: number | null;
    order_value: number | null;
    driver: DriverItem | null;
    total_price: number | null;
    status: Status | null;
    task: null | {
      lat: string;
      lng: string;
    };
    label_dispatched_at: string | null;
    missed_orders?: SingleOrderItem[]; // Array of missed orders
  };
}

export interface PickupRequest {
  full_name?: string;
  phone?: string;
  address_text?: string;
  lat?: string;
  lng?: string;
  email?: string;
  type?: string;
  default?: boolean;
  region_uid?: string;
  description?: string;
}

export interface DownloadPickupLabel {
  uid: string;
  status: 'SUCCESS' | 'PENDING' | 'FAILED';
  name: string;
  created_at: string;
  updated_at: string;
  deleted_at: null;
  file: {
    uid: string;
    platform_code: string;
    item_type: string;
    priority: 0;
    filename: string;
    extension: string;
    abs_url: string;
    type: string;
    used_as: string;
    created_at: string;
    updated_at: string;
    deleted_at: null;
  };
}
